import { ListViewColumnDefinition } from '@components/UI-Components/StandardTableView/models/ListviewColumnDefinition'
import { WholesaleGridCard } from '@components/UI-Components/WholesaleGridCard'
import { config } from '@config'
import { Divider } from '@material-ui/core'
import { ProductContainerRow } from '@modules/collection/components/ProductContainerRow'
import { CardSkeleton } from '@modules/common/component/CardSkeleton'
import { ListRowSkeleton } from '@modules/common/component/ListRowSkeleton'
import { useBuyingSessionGroup } from '@modules/wholesale/buyingGroupSummary/graphql/hooks'
import {
  BuyingSessionAssortmentType,
  ChannelSubSection,
  ViewTabs
} from '@modules/common/models/enums'
import {
  useProductEnumList,
  useBuyingSessionProduct
} from '@modules/wholesale/buyingSessionGroupProductWrapper/graphql/hooks'
import { getEmptyArrayOfLength, isWholesaleCMOrAdmin } from '@services/commonServices'
import { getExchangeRateForClientCurrency } from '@services/wholesale/OrderManagementService'
import { wholeSaleProductService } from '@services/wholesale/productService'
import { wholesalePriceService } from '@services/wholesale/wholesalePriceService'
import { stores, strings } from '@stores'
import classNames from 'classnames'
import map from 'lodash/map'
import { useObserver } from 'mobx-react-lite'
import React, { useEffect, useMemo } from 'react'
import { StandardTreeViewStore } from '@components/UI-Components/StandardTreeView/Store/TreeViewStore'
import { VIPChannel } from '@modules/common/models/enums/VIPChannel'
import { productForecastUIStore } from '@modules/wholesale/buyingSessionGroupProductWrapper/stores/productForecastUIStore'
import { useQueryGetClusterStatus } from '@modules/wholesale/clusterSetting/graphql/getClusterStatus'
import { isRetailChannel } from '@services/wholesale/wholesaleBuyingSessionGroupsServices'
import isString from 'lodash/isString'
import ClusterStatusDropdown from '../components/ClusterStatusDropDown'
import { useAssortmentProduct, useSummary } from '../graphql/hooks'
import * as css from './WsAssortmentProductRowWrapper.css'
import { counterOfferUIStore } from '@modules/wholesale/buyingSessionGroupProductWrapper/stores/CounterOffer/CounterOfferUIStore'
import { getProducts } from './utils'
import { useTranslation } from 'react-i18next'
import { DefaultConfig } from '@constants'

interface IwsAssortmentProductRowWrapper {
  style?: any
  node?: any
  stickyStyle?: any
  listHeaders?: Array<ListViewColumnDefinition>
  canTableFitInView?: boolean
}

export const WsAssortmentProductRowWrapper: React.FunctionComponent<
  IwsAssortmentProductRowWrapper
> = ({ node, style, stickyStyle, listHeaders, canTableFitInView }) => {
  return useObserver(function useHook() {
    const { t } = useTranslation()
    const { category: activity, childrenMeta, childrenMetaValues, categoryIds } = node
    const {
      wsBuyingSessionProduct: {
        handleTagDrop,
        filterProductsBasedOnFilterAndSearch,
        setRefetch,
        retailBuyProducts,
        retailBuyComments,
        whereInputForBSPForBS,
        assortmentProduct,
        sortProductsBasedOnSortType,
        retailBuyProductsLength
      },
      nav: {
        queryParams: {
          assortmentType,
          productLayout,
          tab,
          buyingSessionGroupId,
          assortment,
          subSection,
          buyingSession,
          zoneId,
          client
        },
        onProductDetailClick
      },
      search: { searchKey },
      filterModalStore: { wsAppliedFilters },
      buyingSessionProductPriceStore: {
        getPriceByBuyingSessionAndClient,
        fetchPriceData
      },
      attributeSortStore: { selectedSortType }
    } = stores
    const { init, productForecastData, upsertForecastData, targetCurrency } =
      productForecastUIStore
    const { selectedNavItems } = StandardTreeViewStore
    const { rmClustersData } = counterOfferUIStore

    // create mock array
    const mockProducts = getEmptyArrayOfLength(node.products)

    // Query buying session group in order to get buying session closed date
    const { data: buyingSessionGroupData } = useBuyingSessionGroup(buyingSessionGroupId)

    const queryVariableObject = whereInputForBSPForBS(
      activity,
      childrenMeta,
      childrenMetaValues
    )

    /**
     * Query:
     * 1. Assortment products -> Actual products to be rendered in view
     * 2. EnumList -> For resolving enum attributes
     * 3. Fetch retail cluster status
     * 4. Buying session products -> products to be rendered in Rtl Buy view
     * 5. Get client price list data
     * 6. Get client price list data by doorid
     */
    const [
      { items, loading, refetch },
      { data: productEnumList, loading: enumLoading },
      { items: retailClusterStatuses = [] },
      { data, loading: bspLoading },
      { summary: clientSummaryData }
    ] = [
      useAssortmentProduct(subSection, assortmentType, assortment, buyingSession, {
        activity,
        childrenMeta,
        childrenMetaValues
      }),
      useProductEnumList(),
      useQueryGetClusterStatus(
        isRetailChannel(buyingSessionGroupData?.buyingSessionGroup?.channels)
          ? VIPChannel.Retail
          : null
      ),
      useBuyingSessionProduct(queryVariableObject, 'cache-and-network'),
      useSummary(subSection === ChannelSubSection.Retail ? zoneId : client, 'client')
    ]
    useEffect(() => {
      assortmentType === BuyingSessionAssortmentType.cluster && refetch()
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [node.products])
    useEffect(() => {
      setRefetch(refetch)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refetch])
    // TODO: define a custom hook, this is a duplicate code block
    React.useEffect(() => {
      if (productLayout === 'list') {
        if (!selectedNavItems) return

        const initCategoryValues = selectedNavItems?.some(navItem => {
          if (!isString(navItem)) return false
          const items = navItem?.split(':')
          return (
            items?.[0] === activity &&
            childrenMetaValues?.some(
              child => items?.[1] === child || items?.[2] === child
            )
          )
        })
        init(
          {
            buyingSessionGroupId,
            buyingSessionId: buyingSession,
            clientId: zoneId,
            categoryValues: [activity, ...(childrenMetaValues || [])]
          },
          initCategoryValues,
          false
        )
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      buyingSessionGroupId,
      buyingSession,
      client,
      zoneId,
      // eslint-disable-next-line react-hooks/exhaustive-deps
      JSON.stringify(categoryIds),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      JSON.stringify(selectedNavItems),
      productLayout
    ])

    const isGridView = productLayout === strings.grid
    const isRtlBuyView = ViewTabs.RTL_BUY.toLowerCase() === tab

    const products = getProducts(
      isRtlBuyView,
      assortmentType,
      assortmentProduct,
      data?.findVIPBuyingSessionProduct?.Items,
      filterProductsBasedOnFilterAndSearch,
      searchKey,
      wsAppliedFilters,
      items,
      data,
      sortProductsBasedOnSortType,
      retailBuyProducts,
      selectedSortType,
      retailBuyProductsLength,
      zoneId,
      node
    )

    const productEnums = productEnumList?.findEnumList?.Items ?? []
    const clientCurrency = clientSummaryData?.WHLPriceList?.Currency?.Name
    let wholesalePriceGroup = useMemo(
      () =>
        assortmentType !== BuyingSessionAssortmentType.cluster &&
        clientSummaryData?.WHLPriceList?.PriceGroup,
      [assortmentType, clientSummaryData?.WHLPriceList?.PriceGroup]
    )

    const retailPriceGroup = useMemo(
      () =>
        assortmentType !== BuyingSessionAssortmentType.cluster &&
        clientSummaryData?.RTLPriceList?.PriceGroup,
      [assortmentType, clientSummaryData?.RTLPriceList?.PriceGroup]
    )

    let clientId = clientSummaryData?.Id
    const exchangeRatesForGlobalCurrency =
      buyingSessionGroupData?.buyingSessionGroup?.CurrencyExchangeTable

    const variableForFetchingPrices = useMemo(
      () =>
        wholesalePriceService.getVariableForFetchingPrices(
          retailPriceGroup,
          wholesalePriceGroup
        ),
      [retailPriceGroup, wholesalePriceGroup]
    )

    const priceTypeKey = useMemo(
      () => (clientId ? buyingSessionGroupId + clientId : buyingSessionGroupId),
      [buyingSessionGroupId, clientId]
    )
    useEffect(() => {
      if (buyingSessionGroupId) {
        fetchPriceData(variableForFetchingPrices, buyingSessionGroupId, priceTypeKey)
      }
    }, [buyingSessionGroupId, fetchPriceData, priceTypeKey, variableForFetchingPrices])

    return (
      <React.Fragment>
        <div style={style} className={classNames({ [css.root]: isGridView })}>
          {isGridView ? (
            assortmentProduct.loading || bspLoading || enumLoading || loading ? (
              map(mockProducts, (product, index) => <CardSkeleton key={index} />)
            ) : (
              map(
                wholeSaleProductService.processProductsForGridView(
                  products,
                  productEnums,
                  getPriceByBuyingSessionAndClient(
                    assortmentType === BuyingSessionAssortmentType.cluster
                      ? buyingSessionGroupId
                      : priceTypeKey
                  ),
                  assortmentType,
                  t,
                  buyingSessionGroupData?.buyingSessionGroup?.channels
                ),
                (product, index) => (
                  <WholesaleGridCard
                    product={product}
                    key={product.productId}
                    handleTagDrop={handleTagDrop}
                    clickHandler={onProductDetailClick(product.node.Id)}
                  />
                )
              )
            )
          ) : assortmentProduct.loading || bspLoading || enumLoading || loading ? (
            map(mockProducts, (product, index) => (
              <React.Fragment key={'ListRowSkeleton' + index}>
                <ListRowSkeleton />
                <Divider />
              </React.Fragment>
            ))
          ) : (
            <ProductContainerRow
              isWholesale={true}
              headers={listHeaders}
              stickyStyle={stickyStyle}
              canTableFitInView={canTableFitInView}
              products={wholeSaleProductService.processProductsForListViewData(
                products,
                retailBuyProducts,
                retailBuyComments,
                rmClustersData,
                productEnums,
                tab as any,
                getPriceByBuyingSessionAndClient(
                  assortmentType === BuyingSessionAssortmentType.cluster
                    ? buyingSessionGroupId
                    : priceTypeKey
                ),
                assortmentType,
                t,
                assortmentType === strings.client.toLowerCase()
                  ? {
                      targetCurrency: isWholesaleCMOrAdmin()
                        ? config.globalCurrency || DefaultConfig.GLOBAL_CURRENCY
                        : clientCurrency,
                      convertToTargetCurrency: isWholesaleCMOrAdmin() ? true : false,
                      exchangeRate:
                        clientCurrency ===
                        (config.globalCurrency || DefaultConfig.GLOBAL_CURRENCY)
                          ? 1
                          : getExchangeRateForClientCurrency(
                              clientCurrency,
                              exchangeRatesForGlobalCurrency
                            )
                    }
                  : null,
                { productForecastData, upsertForecastData, targetCurrency }
              )}
            />
          )}
        </div>
        <ClusterStatusDropdown clusterStatuses={retailClusterStatuses} />
      </React.Fragment>
    )
  })
}
